/* FF PERSISTENT CART REMINDER (2026-08-13, wip252; owner-review fixes wip263, wip275)
 *
 * wip395 (2026-08-17, owner-picked mockup "O1 - One bar"): the sticky ATC bar itself now
 * carries the cart on mobile (own glyph + live badge, template-scoped in
 * sections/ff-main-product-redesign.liquid / assets/ff-pdp.css -- this file does not render
 * the glyph). Two changes here: (1) render() now dispatches a real 'ff:cart-count' DOM event
 * with the live item count on every cart refresh -- the glyph badge listens for it instead
 * of duplicating this file's own wrapped-fetch cycle. (2) the wip374 mobile pill (below) is
 * now fully suppressed, not just shrunk, in the exact mobile+sticky-bar-present combination
 * (a new class, `.ff-cart-reminder--barcarries`, additive alongside the untouched
 * `.ff-cart-reminder--compact`) -- the bar carries the cart now, so the pill would be a
 * second, redundant cart affordance. Desktop is completely unaffected by either change. See
 * SPEC/GLOBAL.md "One-bar (O1)" and wip395_onebar/VERIFICATION.md for the live-measured proof.
 *
 * wip357 (2026-08-17, owner screenshots + two same-day live-review corrections) — see
 * ff-cart-reminder.css's header comment for the full rationale on both fixes; summary here:
 *  1. DESKTOP: went through several passes the same day. `bottom`/BASE_GAP: 160 -> 250 (+90,
 *     owner's initial "60-100px up" ask) -> corrected to 125 after a live look at the actual
 *     render ("cut it in half for how high it is on the page"). `left`: first pushed right
 *     dynamically to clear the real, measured collection-sidebar edge (proven via live
 *     elementsFromPoint sampling that the original flat 24px genuinely overlapped real filter
 *     checkboxes at 1280/1440/1920) — then REVERTED same day by a second live review ("hug the
 *     left edge... fixed to the left edge, roughly 20-40px"). `left` is now a flat 24px again
 *     (see the desktop-positioning comment further down, near CHAT_LIFT_GAP, for the full
 *     measurement record on why no gutter beside the sidebar is wide enough to matter at any of
 *     the three widths tested).
 *  2. MOBILE: full-width banner replaces the one-line pill. updateGorgiasClearance() (horizontal
 *     Gorgias-dodge, and the MIN_CARD_W/MOBILE_EDGE/CHAT_FALLBACK_CLEAR constants that only existed
 *     to support it) is REMOVED — a full-width banner has no side margin to dodge into. New
 *     updateChatLift() replaces it: measures the real Gorgias launcher and the real banner position
 *     every layout pass and pushes the launcher up (inline `!important` transform, only this file —
 *     assets/base.css and layout/theme.liquid are untouched) exactly far enough to clear the banner,
 *     composing correctly with base.css's own existing sticky-bar lift rather than fighting it.
 *
 * wip374 (2026-08-17, owner phone screenshots) — on MOBILE PRODUCT PAGES specifically (mobile AND
 * a sticky add-to-cart bar on screen, i.e. .ff-cart-reminder--compact), the wip357 full-width
 * banner ate too much screen alongside that bar and collided with the Gorgias launcher. The CSS
 * (see ff-cart-reminder.css header + its `@media (max-width:749px)` block) now gives that exact
 * combination a small left-anchored pill/bubble instead of the banner; everything else (mobile
 * with NO sticky bar, and desktop at any sticky state) is visually unchanged.
 * This file's only change: the View-cart button's label. The bubble shows "View cart" (+ the
 * real item count, e.g. "View cart · 2", per the owner's own example) since it no longer has a
 * separate meta line to show the count on. updateViewButtonLabel(), called from the end of
 * updateStickyState() (which already knows both stickyOn and, now, viewport width), is the only
 * new function — every geometry/docking/chat-lift mechanism below is untouched, including the
 * FLAG TRANSITION when the sticky bar hides again: no new code was needed for that either, since
 * updateStickyState() already toggles the --compact class and recomputes --ff-cr-bottom on every
 * pass the flag changes, and removing --compact alone is what flips the CSS back to the plain
 * banner rule. See ff-cart-reminder.css's header comment and wip374_reminder2/VERIFICATION.md for
 * the full reasoning and the live measured proof of both directions.
 *
 * wip275: wip263's CSS positioned the mobile card with a hardcoded right
 * inset, `right: var(--ff-cr-gorgias-clear, 196px)`, and NOTHING in this file
 * ever set --ff-cr-gorgias-clear — the measurement it was designed around was
 * never written. The 196px fallback therefore always applied and, measured
 * live at 390px, left the card 186px wide and its text column 0px wide, so
 * every word broke one character per line (a 535px-tall vertical letter
 * stack). updateGorgiasClearance() below now actually measures the live chat
 * launcher and, crucially, CLAMPS the value so the card can never be squeezed
 * below MIN_CARD_W no matter what the launcher does. BASE_GAP was also raised
 * to match the card's new `bottom` (off the extreme corner, per the owner).
 *
 * Behaviour (owner-approved, verbatim): "not a nudge — just keep it in the
 * bottom right of the screen" whenever the cart has items. No auto-hide, no
 * timer, no one-shot fire. Only the visitor's own × hides it, for the rest
 * of the browser session (sessionStorage).
 *
 * wip263: moved to bottom-LEFT (the Gorgias chat bubble owns bottom-right —
 * see assets/ff-cart-reminder.css header comment) and added a compact-chip
 * mode. Whenever a sticky add-to-cart / checkout bar is on screen, this
 * reuses the theme's existing body.ff-sticky-atc-on flag (already set by
 * layout/theme.liquid [ff-sticky-atc-toggle] for the PDP sticky buy bar and
 * /cart's own sticky checkout bar) to shrink to a small chip and dock above
 * that bar instead of overlapping it. This runs at ANY viewport width, not
 * just mobile — the flag itself is not mobile-scoped.
 *
 * How it stays live without a page reload: this theme adds to cart through
 * several different code paths (collection/search quick-add fetches
 * /cart/add.js directly, the PDP wraps window.fetch around /cart/add*, the
 * cart page rail calls /cart/add.js with a sections param, etc. — see
 * SPEC/CART.md §11). Rather than hook each one, this wraps window.fetch
 * once and re-reads /cart.js after any call to a /cart/add|change|update|
 * clear endpoint resolves. That covers every existing and future AJAX cart
 * mutation in this theme without touching any of those other files.
 */
(function () {
  'use strict';

  var SESSION_KEY = 'ffCartReminderHidden';
  var LAST_ADDED_KEY = 'ffCartReminderLastAdded';
  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var CART_ADD_RE = /\/cart\/add(\.js)?(\?|$)/;
  // Same sticky-bar selectors the [ff-sticky-atc-toggle] flag in
  // layout/theme.liquid is watching for: PDP sticky buy bars + /cart's own
  // sticky checkout bar. Used to measure real height for the docking offset.
  // FF 2026-08-17 [wip338 item 8]: found live (real PDP, real scroll-past-buybox trigger,
  // not a guess) that this store's ACTUAL sticky bar never carries `.active` or
  // `.visible` -- it is #stickyAtcBar with classes "sticky-atc-bar ff-satc-contain" only,
  // always present in the DOM (even before it visually docks) and ~128px tall once shown.
  // The `.active`/`.visible`-qualified selectors below therefore never matched it, so
  // updateStickyState() always fell through to STICKY_FALLBACK_OFFSET (56px) instead of
  // the real ~128px height -- undershooting the lift by ~72px and leaving the reminder
  // banner genuinely overlapping the sticky bar (measured live: reminder bottom edge sat
  // ~64px INSIDE the bar's own top edge). Added the bare `#stickyAtcBar` id (no modifier
  // class required) alongside the existing selectors, kept for any other template that
  // DOES use them -- safe because the geometry check right below this selector (real
  // height>0 AND bottom within 4px of the viewport edge) is what actually gates "is this
  // bar showing right now", not the class match, so widening the selector cannot cause a
  // false positive on an inactive/off-screen bar.
  var STICKY_SELECTOR =
    '#ff-cart-sticky-bar, #stickyAtcBar, .sticky-atc-bar.active, #stickyAtcBar.active, ' +
    '#bliss-sticky-atc.visible, #olivia-sticky-atc.visible, #haven-sticky-atc.visible';
  var STICKY_FALLBACK_OFFSET = 56; // used only if the flag fires but none of the known selectors match

  // wip275 — the live chat launcher, whatever form it is in right now. This
  // store renders #gorgias-facade-button (a 56px circle at right:20px;
  // bottom:20px) until Gorgias itself loads and replaces it with
  // iframe#chat-button, which assets/base.css [ff-chat-dock] re-docks to
  // right: max(24px, safe-area). Both are measured the same way.
  // wip357: previously also used by updateGorgiasClearance() (horizontal dodge, now removed —
  // see header comment); still used by updateChatLift() (vertical dodge) below.
  var CHAT_SELECTOR =
    '#gorgias-facade-button, iframe#chat-button, #gorgias-chat-container, ' +
    'iframe[id^="gorgias-chat"], .gorgias-chat-key-container, iframe[src*="gorgias"]';
  var CHAT_MAX_LAUNCHER_W = 220; // wider than this is an OPEN chat panel/campaign, not the launcher — ignore it
  var CHAT_LIFT_GAP = 10; // wip357 — clear space kept between the mobile banner's top edge and the chat launcher's bottom edge

  // wip357 desktop horizontal positioning: went through two live-review passes the same day.
  // Pass 1 pushed `left` right dynamically (via a since-removed updateHorizontalPosition(),
  // measuring the real collection-sidebar edge) — REVERTED same day by a second live review:
  // "the desktop popup is currently rendering over the middle of the collection page's product
  // grid — wrong... it must HUG THE LEFT EDGE of the viewport... fixed to the left edge (roughly
  // 20-40px from the left)... at viewport widths where no free gutter exists beside the sidebar,
  // fall back to the plain bottom-left corner with a small offset." Real measurement
  // (elementsFromPoint sampling, same technique as pass 1) at all three widths confirmed there is
  // no usable gutter for a legible card beside the sidebar at 1280/1440 (the sidebar's own real
  // left edge is only ~31/36px in — narrower than any legible card width) and only a ~110px one
  // at 1920 (still far narrower than this card) — i.e. the owner's own explicit fallback ("plain
  // bottom-left corner with a small offset") is what actually applies at every width tested, not
  // an edge case. No JS involvement needed for this any more — `left` is a flat `24px` in
  // ff-cart-reminder.css, same value this card used pre-wip357 (wip275/wip338). See that file's
  // header comment and wip357_reminder/VERIFICATION.md for the full measurement record, including
  // the residual, honestly-documented sidebar overlap this fallback implies at 1280/1440 —
  // matching the owner's own stated contingency for it, not silently claimed as zero-overlap.

  var root, thumbEl, metaEl, viewBtn, closeBtn;
  var cartDrawerEl;
  var sessionHidden = false;
  var lastCart = null;
  var modalOpen = false;
  var lastAdded = null; // { variant_id, ts } — which cart line to show, set from the real /cart/add.js response

  function init() {
    root = document.querySelector('[data-ff-cr]');
    if (!root) return; // not rendered on this page (hero PDP / /cart) — nothing to do

    thumbEl = root.querySelector('[data-ff-cr-thumb]');
    metaEl = root.querySelector('[data-ff-cr-meta]');
    viewBtn = root.querySelector('[data-ff-cr-view]');
    closeBtn = root.querySelector('[data-ff-cr-close]');

    sessionHidden = sessionStorage.getItem(SESSION_KEY) === '1';
    try {
      var storedAdd = sessionStorage.getItem(LAST_ADDED_KEY);
      if (storedAdd) lastAdded = JSON.parse(storedAdd);
    } catch (e) {}

    closeBtn.addEventListener('click', hideForSession);

    cartDrawerEl = document.querySelector('cart-drawer');
    if (cartDrawerEl) {
      modalOpen = cartDrawerEl.classList.contains('active');
      var mo = new MutationObserver(function () {
        modalOpen = cartDrawerEl.classList.contains('active');
        applyVisibility();
      });
      mo.observe(cartDrawerEl, { attributes: true, attributeFilter: ['class'] });
    }

    // Any fixed sticky bar toggling on/off (PDP buy bar, /cart's own sticky
    // checkout bar) flips this body class in this theme already (see
    // layout/theme.liquid [ff-sticky-atc-toggle]) — reuse it as our compact
    // -chip trigger instead of re-detecting every bar's markup ourselves.
    // wip357: was `new MutationObserver(updateStickyState)` — widened to the full
    // updateLayout() so updateChatLift() (which depends on the banner's position,
    // which itself just changed because of this same class flip) recomputes too.
    var bodyObserver = new MutationObserver(updateLayout);
    bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('resize', updateLayout);

    // wip357: the chat launcher is injected late (facade button first, then Gorgias
    // swaps in its own iframe), so re-measure a few times rather than trusting
    // whatever is/isn't in the DOM at init — updateChatLift() (called via
    // updateLayout()) re-reads its real position each time, so a missed early
    // measurement only ever costs a brief unlifted moment, never a stuck state.
    updateLayout();
    [800, 2500, 6000].forEach(function (ms) { setTimeout(updateLayout, ms); });
    window.addEventListener('load', updateLayout);

    wrapFetch();
    refreshFromServer();
  }

  function wrapFetch() {
    var origFetch = window.fetch;
    if (typeof origFetch !== 'function') return;
    window.fetch = function () {
      var urlArg = arguments[0];
      var url = typeof urlArg === 'string' ? urlArg : (urlArg && urlArg.url) || '';
      var promise = origFetch.apply(this, arguments);
      if (CART_ADD_RE.test(url)) {
        // Read the real added-item payload so "most recently added" is exact —
        // cart.js's own items[] order is NOT reliably insertion-order in this
        // theme (multiple cart features re-sort/re-render the line list), so
        // guessing from array position picked the wrong thumbnail in testing.
        promise.then(function (response) {
          response.clone().json().then(captureAddedItem).catch(function () {});
        }).catch(function () {});
      }
      if (CART_MUTATION_RE.test(url)) {
        promise.then(function () {
          // Invalidate immediately so no caller's next FFCartState.get() (this file's
          // own or any other site's) can serve a pre-mutation cache within the 400ms
          // TTL window -- this fires sitewide for ANY /cart/add|change|update|clear
          // routed through window.fetch, not just this file's own refresh below.
          if (window.FFCartState) { window.FFCartState.invalidate(); }
          // Let the theme's own cart-drawer/section refresh settle first.
          setTimeout(refreshFromServer, 300);
        }).catch(function () {});
      }
      return promise;
    };
  }

  function captureAddedItem(json) {
    var item = null;
    if (json && json.variant_id) {
      item = json; // single-item /cart/add.js response
    } else if (json && json.items && json.items.length) {
      item = json.items[json.items.length - 1]; // multi-item add payload
    }
    if (!item) return;
    lastAdded = { variant_id: item.variant_id, ts: Date.now() };
    try { sessionStorage.setItem(LAST_ADDED_KEY, JSON.stringify(lastAdded)); } catch (e) {}
  }

  function refreshFromServer() {
    window.FFCartState.get()
      .then(render)
      .catch(function () {});
  }

  function render(cart) {
    lastCart = cart;
    // wip395 (2026-08-17, mockup "O1 - One bar"): broadcast the live item count for anything
    // else on the page that wants to mirror the real cart without re-implementing this file's
    // own wrapped-fetch/refresh cycle -- the actual "cart-update events" this theme has (see
    // wrapFetch()'s header note on why the native cart:updated event isn't used: wip331
    // measured it never fires on this template). Currently consumed by the sticky-bar cart
    // glyph badge, sections/ff-main-product-redesign.liquid (template-scoped, mobile-only).
    // Fired before the empty-cart early return below so a cart hitting zero also broadcasts
    // 0 (badge-hide), not silence.
    document.dispatchEvent(new CustomEvent('ff:cart-count', { detail: { count: (cart && cart.item_count) || 0 } }));
    if (!cart || !cart.item_count) {
      root.hidden = true;
      return;
    }

    var displayItem = null;
    if (lastAdded && lastAdded.variant_id) {
      displayItem = findCartItemByVariant(cart, lastAdded.variant_id);
    }
    if (!displayItem) {
      // No captured add (e.g. first load of a page with an existing cart) or
      // that line is no longer in the cart — best-effort fall back to the
      // last line Shopify itself returns.
      displayItem = cart.items[cart.items.length - 1];
    }
    if (displayItem) {
      thumbEl.src = displayItem.image || '';
      thumbEl.alt = displayItem.product_title || '';
    }
    var n = cart.item_count;
    metaEl.textContent = n + (n === 1 ? ' item' : ' items') + ' · ' + formatMoney(cart.total_price, cart.currency);
    if (viewBtn && window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      viewBtn.href = window.Shopify.routes.root + 'cart';
    }

    applyVisibility();
  }

  function findCartItemByVariant(cart, variantId) {
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].variant_id === variantId) return cart.items[i];
    }
    return null;
  }

  function formatMoney(cents, currency) {
    var amount = cents / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
        currencyDisplay: 'narrowSymbol'
      }).format(amount);
    } catch (e) {
      return '$' + amount.toFixed(2);
    }
  }

  function isCartOrCheckoutPage() {
    var path = window.location.pathname;
    return document.body.classList.contains('ff-template-cart') ||
      /^\/cart(\/|$)/.test(path) ||
      /^\/checkouts?(\/|$)/.test(path);
  }

  function applyVisibility() {
    if (!root) return;
    if (sessionHidden) { root.hidden = true; return; }
    if (isCartOrCheckoutPage()) { root.hidden = true; return; }
    if (!lastCart || !lastCart.item_count) { root.hidden = true; return; }
    if (modalOpen) { root.hidden = true; return; } // never overlap the add-to-cart modal
    root.hidden = false;
    updateLayout();
  }

  function updateLayout() {
    updateStickyState();
    updateChatLift();
  }

  // wip357 — MOBILE (<750px) vertical Gorgias avoidance, replacing the removed
  // updateGorgiasClearance() (which dodged horizontally; the new banner is full-width and
  // has no side margin to dodge into). STACKING CONTRACT (agreed with the PDP lane):
  // bottom-to-top is sticky add-to-cart bar -> this banner -> the Gorgias launcher.
  // Banner-above-sticky-bar already works via the existing --ff-cr-bottom / body.ff-sticky
  // -atc-on mechanism (wip346, untouched). This function handles the other half: pushing
  // the real Gorgias launcher up clear of the banner's real top edge.
  //
  // Idempotent by construction: every call clears its OWN prior inline override first,
  // before measuring, so the measurement always reflects the launcher's true base.css
  // -driven position (default, or base.css's own -70px/-80px sticky-bar lift) rather than
  // a compounding stack of this function's own past pushes. The extra lift computed is
  // only what's needed BEYOND whatever base.css already provides, applied as an inline
  // `!important` transform — inline + !important outranks base.css's class-based
  // !important rule (same importance tier, inline wins on specificity), so this can
  // reliably override or coexist with it without editing assets/base.css at all.
  function updateChatLift() {
    var els = document.querySelectorAll(CHAT_SELECTOR);
    // wip395: a barcarries-suppressed reminder (mobile + sticky bar, see updateStickyState()
    // above) is hidden via CSS `display:none`, not `root.hidden` -- root.getBoundingClientRect()
    // on a display:none element returns an all-zero rect, which would otherwise compute a
    // wildly wrong (viewport-height-sized) extra lift below. There's nothing of THIS file's to
    // clear above in that state -- the sticky bar itself is lifted clear of by assets/base.css's
    // own [ff-chat-dock] rule (untouched, a separate consumer of the same body.ff-sticky-atc-on
    // flag) -- so this file adds zero extra lift, same as any other "reminder not visible" state.
    var reminderVisible = !!root && !root.hidden && !root.classList.contains('ff-cart-reminder--barcarries');
    var shouldLift = reminderVisible && window.innerWidth < MOBILE_BREAKPOINT;
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      el.style.removeProperty('transform'); // drop our own prior push first — see note above
      if (!shouldLift) continue;
      var b = el.getBoundingClientRect();
      if (b.width <= 0 || b.height <= 0) continue; // not on screen / not loaded yet
      if (b.width > CHAT_MAX_LAUNCHER_W) continue; // open chat panel / campaign, not the launcher
      if (b.bottom < window.innerHeight - 240) continue; // not docked near the bottom — irrelevant
      var bannerRect = root.getBoundingClientRect();
      var neededClearance = (window.innerHeight - bannerRect.top) + CHAT_LIFT_GAP;
      var currentBottomGap = window.innerHeight - b.bottom;
      var extraLift = neededClearance - currentBottomGap;
      if (extraLift > 0) {
        el.style.setProperty('transform', 'translateY(-' + Math.round(extraLift) + 'px)', 'important');
      }
    }
  }

  // Vertical position is written straight to `bottom` via the --ff-cr-bottom
  // custom property. wip263 did this with `transform: translateY(--ff-cr-lift)`
  // instead, on the theory that `bottom` "gets stuck" — that was a
  // misdiagnosis, and it cost the feature its sticky-bar docking: measured
  // live in wip275, --ff-cr-lift moved the card by exactly 0px. The real
  // cause was that BOTH properties were listed in the base rule's
  // `transition:`, and a transition that never gets a frame (any non-
  // compositing / backgrounded document, e.g. the automated browser these
  // checks run in) is pinned at its FROM value forever. Only `opacity` is
  // transitioned now, so this writes through immediately — verified live.
  // BASE_GAP must match the `bottom` fallback in ff-cart-reminder.css.
  // wip275: raised 16 -> 32 with the CSS, per the owner ("bring it a little
  // bit up ... instead of right in the corner"). This constant MUST equal the
  // `bottom` on `.ff-cart-reminder` in ff-cart-reminder.css or every dynamic
  // offset below is wrong by the difference.
  // wip338 item 8 (2026-08-17): 32 -> 160, per Tyson ("reposition noticeably higher than
  // the bottom corner ... lifted ~120-200px").
  // wip357 (2026-08-17): 160 -> 250 (+90, inside the owner's fresh "60-100px up" ask; this
  // pass also pushed `left` right dynamically — see ff-cart-reminder.css's header comment).
  // wip357 live-review correction (2026-08-17, same day, owner looking at the actual render):
  // "the pushing right is perfect. But cut it in half for how high it is on the page" ->
  // 250 -> 125. (The rightward `left` push from the same-day pass above was separately
  // reverted by a second correction minutes later — see the desktop-positioning comment near
  // CHAT_LIFT_GAP above; `left` is flat 24px again in the CSS, unrelated to BASE_GAP.)
  // MOBILE_GAP 20 -> 0: the mobile banner is now full-width and edge-to-edge ("like a
  // sticky add-to-cart almost", owner's words) rather than a floating pill with a gap —
  // it sits flush at the bottom on pages with no sticky ATC bar, same as a real sticky
  // add-to-cart bar would (its own safe-area bottom padding is in the CSS instead).
  var BASE_GAP = 28; /* FF 2026-08-17 [wip375] 125->28: now actually matches the CSS fallback + Tyson's "if 50 do 25" ask; wip366 only changed the CSS side and JS kept overwriting it */
  var MOBILE_GAP = 0;
  var MOBILE_BREAKPOINT = 750;

  // Decides compact-chip vs. full card, at ANY viewport width, from the
  // theme's existing body.ff-sticky-atc-on flag — and measures the real
  // sticky bar height so the chip docks flush above it instead of guessing.
  // Deliberate deviation from the owner's "just override the sticky bar"
  // suggestion: we never hide/cover the sticky add-to-cart bar itself (see
  // SPEC/GLOBAL.md "Persistent cart reminder" for the full reasoning).
  function updateStickyState() {
    if (!root || root.hidden) return;
    var stickyOn = document.body.classList.contains('ff-sticky-atc-on');
    var isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    var desiredGap;
    if (stickyOn) {
      var offset = 0;
      var stickyEl = document.querySelector(STICKY_SELECTOR);
      if (stickyEl) {
        var rect = stickyEl.getBoundingClientRect();
        if (rect.height > 0 && rect.bottom > window.innerHeight - 4) {
          offset = rect.height;
        }
      }
      if (offset === 0) offset = STICKY_FALLBACK_OFFSET; // flag fired but no known selector matched
      desiredGap = offset + 8; // dock 8px clear above the sticky bar's real height
    } else if (isMobile) {
      desiredGap = MOBILE_GAP;
    } else {
      desiredGap = BASE_GAP;
    }
    root.classList.toggle('ff-cart-reminder--compact', stickyOn);
    // wip395 (2026-08-17, mockup "O1 - One bar"): on MOBILE specifically, once a sticky ATC
    // bar is present, the bar itself now carries the cart (its own glyph + live badge,
    // injected template-scoped from sections/ff-main-product-redesign.liquid -- see
    // SPEC/GLOBAL.md "One-bar (O1)"). The wip374 pill directly below is redundant in that
    // exact combination and is suppressed outright (CSS `display:none` inside the <749px
    // media query, ff-cart-reminder.css) rather than shown alongside a second cart
    // affordance. DESKTOP is untouched: `--compact` above still drives the existing compact
    // chip at any width exactly as wip346/wip357/wip374 left it -- this new class is purely
    // additive and only has an effect inside that mobile-scoped CSS selector.
    root.classList.toggle('ff-cart-reminder--barcarries', stickyOn && isMobile);
    root.style.setProperty('--ff-cr-bottom', Math.round(desiredGap) + 'px');
    // wip374: mobile + sticky-bar-present is the new small left-anchored bubble (see
    // ff-cart-reminder.css's `.ff-cart-reminder.ff-cart-reminder--compact` rule inside the
    // <750px media query) — "View cart" + the real item count is its only content besides the
    // close X, so the label needs the count appended here. Every other state (desktop compact
    // chip, plain mobile banner, desktop full card) keeps the static "View cart" text unchanged.
    updateViewButtonLabel(stickyOn && isMobile);
  }

  // wip374 — sets the View-cart button's visible label. Only the mobile bubble
  // (stickyOn && isMobile, passed in as showCount) gets the "· N" item-count suffix, matching
  // the owner's own example ("View cart · 2"); every other state keeps the plain "View cart"
  // text this button has always had. Reads lastCart (set by render(), the same source of truth
  // the meta line uses) rather than re-fetching — this runs on every layout pass, not just after
  // a fresh /cart.js read.
  function updateViewButtonLabel(showCount) {
    if (!viewBtn) return;
    var n = lastCart && lastCart.item_count;
    viewBtn.textContent = (showCount && n) ? 'View cart · ' + n : 'View cart';
  }

  function hideForSession() {
    sessionHidden = true;
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) {}
    root.hidden = true;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
